import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Bell, Mail, MessageSquare, RefreshCw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Expiry Notification Queue — SA / Accounts control panel.
 *
 * Runs a dry-run scan of expiring documents, previews the exact message
 * that would go to each Franchise Owner (email + optional WhatsApp),
 * and lets the operator enqueue them to `notification_queue`. Actual
 * SMTP / WhatsApp send is deliberately NOT wired here (Owner directive:
 * "just get ready with msg we will send it").
 */
export const ExpiryNotificationQueuePanel = ({ session }) => {
  const [threshold, setThreshold] = useState(30);
  const [includeExpired, setIncludeExpired] = useState(true);
  const [includeWhatsApp, setIncludeWhatsApp] = useState(false);
  const [preview, setPreview] = useState(null);
  const [queue, setQueue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const scan = useCallback(async () => {
    if (!session?.token) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/document-expiry/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: session.token, days_threshold: threshold,
          include_expired: includeExpired,
        }),
      });
      const j = await res.json();
      if (!res.ok) { toast.error(j.detail || 'Scan failed'); return; }
      setPreview(j);
      setSelectedIdx(0);
    } finally { setBusy(false); }
  }, [session, threshold, includeExpired]);

  const enqueue = async () => {
    if (!session?.token) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/document-expiry/enqueue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: session.token, days_threshold: threshold,
          include_expired: includeExpired, include_whatsapp: includeWhatsApp,
        }),
      });
      const j = await res.json();
      if (!res.ok) { toast.error(j.detail || 'Enqueue failed'); return; }
      toast.success(`Queued ${j.written} notifications (${j.skipped_duplicates} skipped as duplicates)`);
      await loadQueue();
    } finally { setBusy(false); }
  };

  const loadQueue = useCallback(async () => {
    if (!session?.token) return;
    try {
      const res = await fetch(`${API}/api/document-expiry/queue-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token, status: 'pending' }),
      });
      const j = await res.json();
      setQueue(j);
    } catch { setQueue(null); }
  }, [session]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const markSent = async (id) => {
    const res = await fetch(`${API}/api/document-expiry/mark-sent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: session.token, ids: [id], status: 'sent' }),
    });
    if (res.ok) {
      toast.success('Marked as sent');
      await loadQueue();
    }
  };

  const item = preview?.items?.[selectedIdx];

  return (
    <Card className="border-fuchsia-200 mt-4" data-testid="expiry-notify-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="w-4 h-4 text-fuchsia-700" /> Notification Queue
          <Badge variant="outline" className="ml-2 text-[10px]">SA / Accounts</Badge>
          <Badge className="text-[10px] bg-slate-100 text-slate-700">Send transport not wired yet</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Days Threshold</Label>
            <Input type="number" value={threshold} onChange={(e) => setThreshold(Number(e.target.value) || 30)} className="w-24" data-testid="expiry-threshold" />
          </div>
          <div className="flex items-center gap-2 pt-6">
            <Switch checked={includeExpired} onCheckedChange={setIncludeExpired} data-testid="expiry-inc-expired" />
            <span className="text-xs">Include already-expired</span>
          </div>
          <div className="flex items-center gap-2 pt-6">
            <Switch checked={includeWhatsApp} onCheckedChange={setIncludeWhatsApp} data-testid="expiry-inc-whatsapp" />
            <span className="text-xs">Also queue WhatsApp copy</span>
          </div>
          <Button onClick={scan} disabled={busy} size="sm" variant="outline" data-testid="expiry-scan">
            <RefreshCw className="w-3 h-3 mr-1" /> Scan (dry-run)
          </Button>
          <Button onClick={enqueue} disabled={busy || !preview?.total} size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="expiry-enqueue">
            Enqueue {preview?.total ? `(${preview.total})` : ''}
          </Button>
        </div>

        {preview && (
          <div>
            <p className="text-xs text-slate-600 mb-2">
              <strong>{preview.total}</strong> document(s) match — preview of the exact message that would be sent:
            </p>
            {preview.total > 0 && item ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="lg:col-span-1 border border-slate-200 rounded p-2 max-h-72 overflow-y-auto">
                  {preview.items.map((it, i) => (
                    <button
                      type="button"
                      key={i}
                      onClick={() => setSelectedIdx(i)}
                      className={`w-full text-left p-2 rounded text-xs mb-1 ${i === selectedIdx ? 'bg-fuchsia-100 border border-fuchsia-300' : 'hover:bg-slate-50'}`}
                      data-testid={`expiry-item-${i}`}
                    >
                      <div className="font-medium truncate">{it.name || '—'}</div>
                      <div className="text-[10px] text-slate-500">{it.center} · {it.days_left < 0 ? `expired ${-it.days_left}d ago` : `${it.days_left}d left`}</div>
                    </button>
                  ))}
                </div>
                <div className="lg:col-span-2 space-y-2">
                  <div className="border border-slate-200 rounded p-3 bg-slate-50">
                    <p className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</p>
                    <p className="text-xs font-semibold mt-1">To: {item.email_recipients?.join(', ') || <em className="text-red-600">No recipients found — set centre email or franchise contact</em>}</p>
                    <p className="text-xs mt-1">Subject: <span className="font-mono">{item.email_subject}</span></p>
                    <pre className="text-[11px] whitespace-pre-wrap mt-2 bg-white p-2 rounded border border-slate-200">{item.email_body}</pre>
                  </div>
                  <div className="border border-slate-200 rounded p-3 bg-slate-50">
                    <p className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> WhatsApp (queued only when the switch is on)</p>
                    <pre className="text-[11px] whitespace-pre-wrap mt-2 bg-white p-2 rounded border border-slate-200">{item.whatsapp_body}</pre>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center py-4 text-slate-400 text-xs">No documents expiring in the selected window.</p>
            )}
          </div>
        )}

        {queue && (
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-2">
              Pending Queue
              <Badge variant="outline" className="text-[10px]">{queue.counts?.pending || 0} pending · {queue.counts?.sent || 0} sent · {queue.counts?.failed || 0} failed</Badge>
            </p>
            <div className="overflow-x-auto border border-slate-200 rounded max-h-64 overflow-y-auto">
              <Table>
                <TableHeader className="bg-slate-50 sticky top-0">
                  <TableRow>
                    <TableHead className="text-xs">Centre</TableHead>
                    <TableHead className="text-xs">Channel</TableHead>
                    <TableHead className="text-xs">Recipient</TableHead>
                    <TableHead className="text-xs">Days Left</TableHead>
                    <TableHead className="text-xs">Created</TableHead>
                    <TableHead className="text-xs">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(queue.rows || []).map((r) => (
                    <TableRow key={r.id} data-testid={`queue-row-${r.id}`}>
                      <TableCell className="text-xs font-medium">{r.center}</TableCell>
                      <TableCell className="text-xs">{r.channel}</TableCell>
                      <TableCell className="text-xs">{r.recipient}</TableCell>
                      <TableCell className="text-xs">{r.days_left < 0 ? `expired ${-r.days_left}d` : `${r.days_left}d`}</TableCell>
                      <TableCell className="text-xs">{String(r.created_at).slice(0, 16).replace('T', ' ')}</TableCell>
                      <TableCell className="text-xs">
                        <Button size="sm" variant="outline" onClick={() => markSent(r.id)} data-testid={`queue-mark-sent-${r.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Sent
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(queue.rows || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4 text-slate-400 text-xs">
                        No pending notifications. Run a scan and enqueue.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExpiryNotificationQueuePanel;
